import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSettings } from './context';
import { S } from '../../styles';
import { API, authHeaders } from '../../utils/helpers';
import { SPEC_GROUPS, specExample, starterFor, likelyColors } from './bikeSpecs';
import WebsitePhotos from './WebsitePhotos';
import WebsitePages from './WebsitePages';
import WebsiteSignups from './WebsiteSignups';

// ── Settings › Website: the bikes on the shop's website (30 Oct) ─────────────
//
// Lightspeed fills the list (server/helpers/websiteBikes.js); the shop picks
// what goes on the website. Nothing is listed until someone picks it (Manny,
// 30 Oct). Every action here saves at once through /api/website/admin/*, so
// this group has no keys in the settings object and never shows the save bar.
//
//   New          on the shelf, nobody has looked at it yet
//   On website   picked
//   Drafts       being written, not on the website
//   Dismissed    never on the website
//
// A model merged into another (another color of the same bike) is shown
// under that bike, not in the lists.

const CATEGORIES = ['Mountain', 'Road & Gravel', 'Electric', 'Commuter & Hybrid', 'Kids', 'BMX'];
const TABS = [
  { id: 'new', label: 'New from Lightspeed' },
  { id: 'live', label: 'On website' },
  { id: 'draft', label: 'Drafts' },
  { id: 'hidden', label: 'Dismissed' },
];

const money = (n) => (n == null ? '' : Number(n).toLocaleString('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2, maximumFractionDigits: 2,
}));
const day = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const when = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const sizeText = (sizes) => (sizes || []).map(s => (s.size ? `${s.size}${s.units > 1 ? ` ×${s.units}` : ''}` : `${s.units} in stock`)).join(' · ');
const photoSrc = (p) => (p && p.src ? `${API}${p.src}` : null);

async function api(path, opts = {}) {
  const res = await fetch(`${API}/api/website${path}`, {
    credentials: 'include',
    ...opts,
    headers: { ...authHeaders(!!opts.body && !(opts.body instanceof FormData)), ...(opts.headers || {}) },
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.message || d.error || `Spinboard answered ${res.status}.`);
  return d;
}

// The Website group's own tabs (1 Nov): Pages (the door to the page
// builder), Bikes, Photos and icon, Sign-ups (3 Nov). The open tab is remembered in this browser only.
const WEBSITE_TABS = [['pages', 'Pages'], ['bikes', 'Bikes'], ['photos', 'Photos and icon'], ['signups', 'Sign-ups']];
export default function SettingsWebsite() {
  const { T, isDark } = useSettings();
  const [tab, setTab] = useState(() => {
    try { const t = localStorage.getItem('sb_website_tab'); return WEBSITE_TABS.some(([id]) => id === t) ? t : 'bikes'; } catch (_) { return 'bikes'; }
  });
  const open = (id) => { setTab(id); try { localStorage.setItem('sb_website_tab', id); } catch (_) { /* private window */ } };
  const sub = { fontSize: 12, color: T.textSub, lineHeight: 1.5 };
  const btn = (primary) => ({
    background: primary ? T.btnPrimary : 'transparent', color: primary ? T.btnPrimaryText : T.textSub,
    border: `1px solid ${primary ? T.btnPrimary : T.filterBorder}`, borderRadius: 6, padding: '5px 12px',
    fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
  });
  return (
    <>
      <div role="tablist" style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: `1px solid ${T.cardBorder}` }}>
        {WEBSITE_TABS.map(([id, label]) => (
          <button key={id} role="tab" aria-selected={tab === id} onClick={() => open(id)} style={{
            background: 'transparent', border: 'none', borderBottom: `2px solid ${tab === id ? T.navBorder : 'transparent'}`,
            color: tab === id ? T.text : T.textSub, fontWeight: tab === id ? 700 : 500, padding: '8px 14px', fontSize: 13.5,
            cursor: 'pointer', fontFamily: 'inherit', marginBottom: -1,
          }}>{label}</button>
        ))}
      </div>
      {tab === 'bikes' ? <WebsiteBikes />
        : tab === 'pages' ? <WebsitePages T={T} isDark={isDark} btn={btn} sub={sub} />
        : tab === 'signups' ? <WebsiteSignups T={T} isDark={isDark} btn={btn} sub={sub} />
        : <WebsitePhotos T={T} isDark={isDark} btn={btn} sub={sub} />}
    </>
  );
}

function WebsiteBikes() {
  const { T, isDark } = useSettings();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState('new');
  const [showSoldOut, setShowSoldOut] = useState(false);
  const [query, setQuery] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState(null);
  const [rowBusy, setRowBusy] = useState('');
  const [rowError, setRowError] = useState(null);
  const [editingKey, setEditingKey] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await api('/admin/bikes?soldOut=1'));
      setLoadError('');
    } catch (e) { setLoadError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const tile = isDark ? '#1e2a40' : '#f4f4ef';
  const sub = { fontSize: 12, color: T.textSub, lineHeight: 1.5 };
  const btn = (primary) => ({
    background: primary ? T.btnPrimary : 'transparent', color: primary ? T.btnPrimaryText : T.textSub,
    border: `1px solid ${primary ? T.btnPrimary : T.filterBorder}`, borderRadius: 6, padding: '5px 12px',
    fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
  });

  const sync = async () => {
    setSyncing(true); setSyncNote(null);
    try {
      const r = await api('/admin/bikes/sync', { method: 'POST' });
      setSyncNote({ ok: true, text: `${r.models} bikes on your shelf${r.added ? `, ${r.added} new` : ''}${r.soldOut ? `, ${r.soldOut} sold out since last time` : ''}.` });
      await load();
    } catch (e) { setSyncNote({ ok: false, text: e.message }); }
    setSyncing(false);
  };

  const setSoldOut = async (v) => {
    const before = data;
    setData(d => ({ ...d, settings: { ...(d.settings || {}), soldOutBikes: v } }));
    try { await api('/admin/bike-settings', { method: 'PUT', body: JSON.stringify({ soldOutBikes: v }) }); }
    catch (e) { setData(before); setRowError({ key: 'settings', text: e.message }); }
  };

  // Status changes from the list keep whatever has been written about the bike.
  const setStatus = async (bike, status, mergedInto = null) => {
    setRowBusy(bike.key); setRowError(null);
    try {
      await api(`/admin/bikes/${encodeURIComponent(bike.key)}`, { method: 'PUT', body: JSON.stringify({ status, mergedInto, data: bike.content || {} }) });
      await load();
    } catch (e) { setRowError({ key: bike.key, text: e.message }); }
    setRowBusy('');
  };

  if (loadError && !data) {
    return <div style={{ ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}`, color: T.text }}>
      Could not load your website bikes: {loadError} <button style={btn(false)} onClick={load}>Try again</button>
    </div>;
  }
  if (!data) return <div style={{ color: T.textSub, padding: 20 }}>Loading bikes…</div>;

  const bikes = data.bikes || [];
  const byKey = new Map(bikes.map(b => [b.key, b]));
  const mergedUnder = (key) => bikes.filter(b => b.mergedInto === key);
  const listed = bikes.filter(b => !b.mergedInto);
  const inTab = (b, id) => b.status === id && (id === 'live' || showSoldOut || b.inStock);
  const counts = Object.fromEntries(TABS.map(t => [t.id, listed.filter(b => inTab(b, t.id)).length]));
  const q = query.trim().toLowerCase();
  const rows = listed.filter(b => inTab(b, tab))
    .filter(b => !q || `${b.brand} ${b.name} ${b.lsName} ${b.content?.displayName || ''}`.toLowerCase().includes(q));
  const editing = editingKey ? byKey.get(editingKey) : null;
  const soldOutMode = data.settings?.soldOutBikes || 'hide';

  return (
    <>
      <div style={{ ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color: T.text }}>Bikes on your website</div>
        <div style={{ ...S.settingsCardDesc, color: T.textSub, marginBottom: 12 }}>
          Spinboard reads the bikes on your shelf from Lightspeed every three hours and lists them here.
          Nothing appears on your website until you pick it. Prices, sale prices and sizes in stock stay current on their own.
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={btn(true)} disabled={syncing} onClick={sync}>{syncing ? 'Reading Lightspeed…' : 'Refresh from Lightspeed'}</button>
          <span style={sub}>{data.lastSync ? `Last read ${when(data.lastSync)}.` : 'Not read yet.'}</span>
          {syncing && <span style={sub}>This takes a minute or two; Spinboard paces itself so the register is never slowed.</span>}
          {syncNote && <span style={{ fontSize: 12.5, color: syncNote.ok ? '#2ecc71' : '#e74c3c' }}>{syncNote.text}</span>}
        </div>

        <div style={{ marginTop: 16, background: tile, borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 6 }}>When a bike you picked sells out</div>
          {[
            ['hide', 'Take it off the website', 'It comes back by itself when it is in stock again.'],
            ['show', 'Keep it up, marked sold out', 'For shops that want customers to call about it.'],
          ].map(([v, label, help]) => (
            <label key={v} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 13, color: T.text, marginTop: 4, cursor: 'pointer' }}>
              <input type="radio" name="soldOutBikes" checked={soldOutMode === v} onChange={() => setSoldOut(v)} style={{ accentColor: '#F4622A' }} />
              <span>{label} <span style={sub}>{help}</span></span>
            </label>
          ))}
          {rowError?.key === 'settings' && <div style={{ fontSize: 12, color: '#e74c3c', marginTop: 4 }}>Not saved: {rowError.text}</div>}
        </div>
      </div>

      <div style={{ ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}` }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: tab === t.id ? T.filterActive : T.filterInactive, color: tab === t.id ? T.filterActiveText : T.filterInactiveText,
              border: `1px solid ${tab === t.id ? T.filterActive : T.filterBorder}`, borderRadius: 16, padding: '5px 12px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit',
            }}>{t.label} <span style={{ opacity: 0.75 }}>{counts[t.id]}</span></button>
          ))}
          <span style={{ flex: 1 }} />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Find a bike"
            style={{ background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '5px 9px', color: T.text, fontSize: 13, width: 180, fontFamily: 'inherit' }} />
          {tab !== 'live' && (
            <label style={{ ...sub, display: 'inline-flex', gap: 5, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={showSoldOut} onChange={e => setShowSoldOut(e.target.checked)} style={{ accentColor: '#F4622A' }} /> Include sold out
            </label>
          )}
        </div>

        {!bikes.length ? (
          <div style={{ ...sub, marginTop: 16 }}>
            Spinboard has not read your bikes yet. <strong>Refresh from Lightspeed</strong> reads everything in stock under your Bicycles category.
          </div>
        ) : !rows.length ? (
          <div style={{ ...sub, marginTop: 16 }}>
            {q ? 'No bike here matches that.' : {
              new: 'Nothing new. Every bike on your shelf has been picked, drafted or dismissed.',
              live: 'No bikes on your website yet. Pick them from New from Lightspeed.',
              draft: 'No drafts.',
              hidden: 'Nothing dismissed.',
            }[tab]}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
            {rows.map(b => {
              const extra = mergedUnder(b.key);
              const main = (b.content?.photos || [])[0];
              const busy = rowBusy === b.key;
              const inStockAll = [b, ...extra].some(x => x.inStock);
              return (
                <div key={b.key} style={{ background: tile, borderRadius: 8, padding: '10px 12px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ width: 72, height: 48, borderRadius: 6, background: '#fff', overflow: 'hidden', flexShrink: 0,
                    border: `1px ${main ? 'solid' : 'dashed'} ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {main ? <img src={photoSrc(main)} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      : <span style={{ fontSize: 10, color: '#8a94a6' }}>No photo</span>}
                  </div>
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: T.text }}>
                      {b.content?.brand || b.brand} {b.content?.displayName || b.name}
                      {!inStockAll && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#e67e22' }}>
                        SOLD OUT{b.status === 'live' ? (soldOutMode === 'show' ? ' · shown as sold out' : ' · off the website until restocked') : ''}
                      </span>}
                    </div>
                    <div style={sub}>
                      {b.content?.category || b.category}
                      {b.price != null && <> · {money(b.price)}{b.msrp ? <span> (was {money(b.msrp)})</span> : null}</>}
                      {b.sizes?.length ? <> · {sizeText(b.sizes)}</> : null}
                      {b.firstSeen && <> · first seen {day(b.firstSeen)}</>}
                    </div>
                    {extra.length > 0 && (
                      <div style={sub}>Also: {extra.map(x => `${x.name}${x.sizes?.length ? ` (${sizeText(x.sizes)})` : x.inStock ? '' : ' (sold out)'}`).join('; ')}</div>
                    )}
                    {rowError?.key === b.key && <div style={{ fontSize: 12, color: '#e74c3c' }}>{rowError.text}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {tab === 'new' && <>
                      <button style={btn(true)} disabled={busy} onClick={() => setEditingKey(b.key)}>Pick</button>
                      <button style={btn(false)} disabled={busy} onClick={() => setStatus(b, 'hidden')}>Dismiss</button>
                    </>}
                    {tab === 'live' && <>
                      <button style={btn(false)} disabled={busy} onClick={() => setEditingKey(b.key)}>Edit</button>
                      <button style={btn(false)} disabled={busy} onClick={() => setStatus(b, 'draft')}>Take off website</button>
                    </>}
                    {tab === 'draft' && <>
                      <button style={btn(true)} disabled={busy} onClick={() => setEditingKey(b.key)}>Edit</button>
                      <button style={btn(false)} disabled={busy} onClick={() => setStatus(b, 'hidden')}>Dismiss</button>
                    </>}
                    {tab === 'hidden' && (
                      <button style={btn(false)} disabled={busy} onClick={() => setStatus(b, 'draft')}>Restore to drafts</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <BikeEditor key={editing.key} bike={editing} bikes={bikes} T={T} tile={tile} sub={sub} btn={btn}
          onClose={() => setEditingKey(null)}
          onSaved={async () => { await load(); }}
        />
      )}
    </>
  );
}

// ── The content builder for one bike ─────────────────────────────────────────
function BikeEditor({ bike, bikes, T, tile, sub, btn, onClose, onSaved }) {
  const c = bike.content || {};
  const [form, setForm] = useState({
    displayName: c.displayName || '', brand: c.brand || '', category: c.category || '', modelYear: c.modelYear || '',
    blurb: c.blurb || '', photos: c.photos || [], specs: c.specs?.length ? c.specs : [],
    brandUrl: c.brandUrl || '', videoUrl: c.videoUrl || '', featured: !!c.featured,
  });
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(0);
  const [mergePick, setMergePick] = useState('');
  const [showAllBikes, setShowAllBikes] = useState(false);
  const [specPick, setSpecPick] = useState('');
  const [focusSpec, setFocusSpec] = useState(-1);
  const fileRef = useRef(null);
  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const input = { background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '6px 9px', color: T.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', width: '100%' };
  const label = { fontSize: 12, fontWeight: 600, color: T.text, display: 'block', marginBottom: 4 };
  const merged = bikes.filter(b => b.mergedInto === bike.key);
  // Other colors: the likely ones first (same brand, same first words of the
  // name); any other free bike only on request, for the odd case.
  const likely = likelyColors(bike, bikes);
  const others = bikes.filter(b => b.key !== bike.key && !b.mergedInto && !bikes.some(x => x.mergedInto === b.key) && !likely.includes(b))
    .sort((a, b2) => a.brand.localeCompare(b2.brand) || a.name.localeCompare(b2.name));
  const othersByBrand = others.reduce((m, b) => { (m[b.brand] = m[b.brand] || []).push(b); return m; }, {});

  const used = new Set(form.specs.map(s => s.label.trim().toLowerCase()));
  const addSpec = (value) => {
    if (value === '__starter') {
      const add = starterFor(form.category || bike.category).filter(l => !used.has(l.toLowerCase())).map(l => ({ label: l, value: '' }));
      set({ specs: [...form.specs, ...add] });
    } else if (value === '__custom') {
      setFocusSpec(form.specs.length);
      set({ specs: [...form.specs, { label: '', value: '', custom: true }] });
    } else if (value) {
      setFocusSpec(form.specs.length);
      set({ specs: [...form.specs, { label: value, value: '' }] });
    }
    setSpecPick('');
  };
  const moveSpec = (i, d) => setForm(f => {
    const p = [...f.specs]; const j = i + d;
    if (j < 0 || j >= p.length) return f;
    [p[i], p[j]] = [p[j], p[i]];
    return { ...f, specs: p };
  });

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setError('');
    for (const file of list) {
      setUploading(n => n + 1);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const d = await api('/admin/photo', { method: 'POST', body: fd });
        setForm(f => ({ ...f, photos: [...f.photos, { key: d.key, src: d.src, alt: '' }] }));
      } catch (e) { setError(`${file.name}: ${e.message}`); }
      setUploading(n => n - 1);
    }
    if (fileRef.current) fileRef.current.value = '';
  };
  const movePhoto = (i, d) => setForm(f => {
    const p = [...f.photos]; const j = i + d;
    if (j < 0 || j >= p.length) return f;
    [p[i], p[j]] = [p[j], p[i]];
    return { ...f, photos: p };
  });

  const save = async (status) => {
    setBusy(status); setError('');
    try {
      await api(`/admin/bikes/${encodeURIComponent(bike.key)}`, { method: 'PUT', body: JSON.stringify({ status, data: form }) });
      await onSaved();
      onClose();
    } catch (e) { setError(e.message); setBusy(''); }
  };

  // Merging saves at once: it changes another bike, not this form.
  const mergeIn = async (other, into) => {
    setBusy('merge'); setError('');
    try {
      await api(`/admin/bikes/${encodeURIComponent(other.key)}`, {
        method: 'PUT', body: JSON.stringify({ status: other.status === 'new' ? 'draft' : other.status, mergedInto: into, data: other.content || {} }),
      });
      setMergePick('');
      await onSaved();
    } catch (e) { setError(e.message); }
    setBusy('');
  };

  const title = `${form.brand || bike.brand} ${form.displayName || bike.name}`;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '40px 16px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 10, width: '100%', maxWidth: 780, padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: T.text, flex: 1 }}>{title}</div>
          <button style={{ ...btn(false), border: 'none', fontSize: 18 }} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div style={{ ...sub, background: tile, borderRadius: 8, padding: '8px 12px', marginTop: 10 }}>
          <div><strong style={{ color: T.text }}>From Lightspeed</strong> (changes there, not here)</div>
          <div>{bike.lsName}</div>
          <div>
            {bike.categoryPath} · {bike.price != null ? money(bike.price) : 'no price'}{bike.msrp ? ` (was ${money(bike.msrp)})` : ''}
            {' · '}{bike.inStock ? sizeText(bike.sizes) || 'in stock' : 'sold out'}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginTop: 16 }}>
          <div style={{ gridColumn: 'span 2' }}>
            <span style={label}>Name on the website</span>
            <input style={input} value={form.displayName} placeholder={bike.name} onChange={e => set({ displayName: e.target.value })} />
          </div>
          <div>
            <span style={label}>Brand</span>
            <input style={input} value={form.brand} placeholder={bike.brand} onChange={e => set({ brand: e.target.value })} />
          </div>
          <div>
            <span style={label}>Category</span>
            <select style={input} value={form.category} onChange={e => set({ category: e.target.value })}>
              <option value="">{bike.category}</option>
              {CATEGORIES.filter(x => x !== bike.category).map(x => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <span style={label}>Model year</span>
            <input style={input} value={form.modelYear} placeholder="2026" inputMode="numeric" maxLength={4} onChange={e => set({ modelYear: e.target.value.replace(/\D/g, '') })} />
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <span style={label}>Photos</span>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {form.photos.map((p, i) => (
              <div key={p.key} style={{ width: 150, background: tile, borderRadius: 8, padding: 6 }}>
                <div style={{ height: 96, background: '#fff', borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={photoSrc(p)} alt={p.alt || ''} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                </div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 4 }}>
                  {i === 0 ? <span style={{ fontSize: 11, fontWeight: 600, color: '#2ecc71', flex: 1 }}>Main photo</span> : <span style={{ flex: 1 }} />}
                  <button style={{ ...btn(false), padding: '1px 6px' }} disabled={i === 0} onClick={() => movePhoto(i, -1)} title="Move earlier">←</button>
                  <button style={{ ...btn(false), padding: '1px 6px' }} disabled={i === form.photos.length - 1} onClick={() => movePhoto(i, 1)} title="Move later">→</button>
                  <button style={{ ...btn(false), padding: '1px 6px', color: '#e74c3c' }} onClick={() => set({ photos: form.photos.filter((_, j) => j !== i) })} title="Remove">×</button>
                </div>
                <input style={{ ...input, marginTop: 4, fontSize: 11.5, padding: '3px 6px' }} value={p.alt || ''} placeholder="Describe it (for screen readers)"
                  onChange={e => set({ photos: form.photos.map((x, j) => (j === i ? { ...x, alt: e.target.value } : x)) })} />
              </div>
            ))}
            <button onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading > 0}
              style={{ width: 150, minHeight: 96, border: `1px dashed ${T.border}`, borderRadius: 8, background: 'transparent', color: T.textSub, cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit' }}>
              {uploading > 0 ? `Uploading ${uploading}…` : '+ Add photos'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => upload(e.target.files)} />
          </div>
          <div style={{ ...sub, marginTop: 4 }}>The first photo is the one shown in bike lists. Phone photos are fine; Spinboard shrinks them.</div>
        </div>

        <div style={{ marginTop: 16 }}>
          <span style={label}>About this bike</span>
          <textarea style={{ ...input, minHeight: 90, resize: 'vertical' }} value={form.blurb} maxLength={4000}
            placeholder="A few lines in your own words: who it's for, what it's like to ride, why you carry it."
            onChange={e => set({ blurb: e.target.value })} />
        </div>

        <div style={{ marginTop: 16 }}>
          <span style={label}>Key specs <span style={{ ...sub, fontWeight: 400 }}>optional · shown on the bike's page in this order · rows left empty are not saved</span></span>
          {form.specs.map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <input style={{ ...input, width: 180, flex: 'none', fontWeight: 600 }} value={s.label} placeholder="Spec name, e.g. Pedals" maxLength={60}
                autoFocus={focusSpec === i && !s.label}
                onChange={e => set({ specs: form.specs.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
              <input style={input} value={s.value} placeholder={specExample(s.label) ? `e.g. ${specExample(s.label)}` : 'What it is'} maxLength={200}
                autoFocus={focusSpec === i && !!s.label}
                onChange={e => set({ specs: form.specs.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) })} />
              <button style={{ ...btn(false), padding: '4px 7px' }} disabled={i === 0} onClick={() => moveSpec(i, -1)} title="Move up">↑</button>
              <button style={{ ...btn(false), padding: '4px 7px' }} disabled={i === form.specs.length - 1} onClick={() => moveSpec(i, 1)} title="Move down">↓</button>
              <button style={{ ...btn(false), padding: '4px 7px', color: '#e74c3c' }} onClick={() => set({ specs: form.specs.filter((_, j) => j !== i) })} title="Remove">×</button>
            </div>
          ))}
          <select style={{ ...input, width: 'auto', maxWidth: '100%' }} value={specPick} onChange={e => addSpec(e.target.value)} disabled={form.specs.length >= 30}>
            <option value="">{form.specs.length >= 30 ? 'That is the most a bike can have (30)' : '+ Add a spec…'}</option>
            <option value="__starter">The usual specs for {form.category || bike.category} bikes</option>
            <option value="__custom">Your own spec (type its name)</option>
            {SPEC_GROUPS.map(g => (
              <optgroup key={g.group} label={g.group}>
                {g.specs.map(([l]) => <option key={l} value={l} disabled={used.has(l.toLowerCase())}>{l}</option>)}
              </optgroup>
            ))}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginTop: 16 }}>
          <div>
            <span style={label}>Brand's page for this bike</span>
            <input style={input} value={form.brandUrl} placeholder="https://… full specs and geometry" onChange={e => set({ brandUrl: e.target.value })} />
          </div>
          <div>
            <span style={label}>Video</span>
            <input style={input} value={form.videoUrl} placeholder="https://youtube.com/…" onChange={e => set({ videoUrl: e.target.value })} />
          </div>
        </div>
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13, color: T.text, marginTop: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.featured} onChange={e => set({ featured: e.target.checked })} style={{ accentColor: '#F4622A' }} />
          Featured <span style={sub}>shown first where bike sections ask for featured bikes</span>
        </label>

        <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${T.cardBorder}` }}>
          <span style={label}>Other colors of this bike</span>
          <div style={sub}>
            Lightspeed lists each color as a separate bike. Add the other colors here and the website shows one listing,
            with every color's sizes in stock. Takes effect right away; Separate undoes it.
          </div>
          {merged.map(m => (
            <div key={m.key} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, fontSize: 13, color: T.text }}>
              <span style={{ flex: 1 }}>{m.brand} {m.name} <span style={sub}>{m.inStock ? sizeText(m.sizes) : 'sold out'}</span></span>
              <button style={btn(false)} disabled={!!busy} onClick={() => mergeIn(m, null)}>Separate</button>
            </div>
          ))}
          {likely.length > 0 ? (
            <div style={{ marginTop: 10 }}>
              <div style={{ ...sub, fontWeight: 600, color: T.text }}>Looks like the same bike in another color</div>
              {likely.map(m => (
                <div key={m.key} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, fontSize: 13, color: T.text, background: tile, borderRadius: 6, padding: '6px 10px' }}>
                  <span style={{ flex: 1 }}>{m.brand} {m.name} <span style={sub}>{m.inStock ? sizeText(m.sizes) : 'sold out'}{m.price != null ? ` · ${money(m.price)}` : ''}</span></span>
                  <button style={btn(false)} disabled={!!busy} onClick={() => mergeIn(m, bike.key)}>Add</button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ ...sub, marginTop: 8 }}>No other colors of this bike are on your shelf right now.</div>
          )}
          {!showAllBikes ? (
            others.length > 0 && <button style={{ ...btn(false), border: 'none', padding: '6px 0', textDecoration: 'underline' }} onClick={() => setShowAllBikes(true)}>
              Not listed? Choose from all your bikes
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <select style={input} value={mergePick} onChange={e => setMergePick(e.target.value)}>
                <option value="">Choose a bike…</option>
                {Object.entries(othersByBrand).map(([brand, list]) => (
                  <optgroup key={brand} label={brand}>
                    {list.map(m => <option key={m.key} value={m.key}>{m.name}{m.inStock ? '' : ' (sold out)'}</option>)}
                  </optgroup>
                ))}
              </select>
              <button style={btn(false)} disabled={!mergePick || !!busy} onClick={() => mergeIn(bikes.find(b => b.key === mergePick), bike.key)}>Add</button>
            </div>
          )}
        </div>

        {error && <div style={{ fontSize: 12.5, color: '#e74c3c', marginTop: 14 }}>Not saved: {error}</div>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 18, alignItems: 'center' }}>
          <button style={btn(true)} disabled={!!busy || uploading > 0} onClick={() => save('live')}>{busy === 'live' ? 'Saving…' : bike.status === 'live' ? 'Save' : 'Put on website'}</button>
          <button style={btn(false)} disabled={!!busy || uploading > 0} onClick={() => save('draft')}>{busy === 'draft' ? 'Saving…' : bike.status === 'live' ? 'Save and take off website' : 'Save as draft'}</button>
          <span style={{ flex: 1 }} />
          <button style={{ ...btn(false), border: 'none' }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
